import React, { useEffect, useState } from 'react';
import styled, { css } from 'styled-components';

import { CommonTheme, useCurrentTheme } from 'app/shared/theme';
import { useButtonClickBehavior } from 'app/shared/utils/useButtonClickBehavior';
import {
  withButtonStyleCSS,
  WithButtonStyleProps,
  withCursorCSS,
  WithCursorProps,
  withDimensionsCSS,
  WithDimensionsProps,
  withShadowCSS,
  WithShadowProps,
  withSpacingCSS,
  WithSpacingProps,
} from 'app/shared/components/styleUtils';
import { Icon, ThemeWrapper } from 'app/shared/components/atoms';
import { Box } from 'app/shared/components/atoms';
import { ReactComponent as Loading } from 'icons/loading.svg';

export interface ActionButtonProps
  extends React.ComponentPropsWithoutRef<'button'>,
    WithSpacingProps,
    WithDimensionsProps,
    React.AriaAttributes,
    WithCursorProps,
    WithShadowProps,
    WithButtonStyleProps {
  trailingIcon?: React.FC<
    React.PropsWithChildren<React.SVGProps<SVGSVGElement>>
  >;
  leadingIcon?: React.FC<
    React.PropsWithChildren<React.SVGProps<SVGSVGElement>>
  >;
  'data-qaid': string;
  flex?: React.CSSProperties['flex'];
}

interface ActionButtonCSSProps extends ActionButtonProps {
  theme: CommonTheme;
}

// Keep withButtonStyleCSS last on the list to avoid styling (margin) issues on
// secondary and tertiary button variants
const StyledButton = styled.button<ActionButtonCSSProps>`
  ${withCursorCSS};
  ${withSpacingCSS};
  ${withDimensionsCSS};
  ${withButtonStyleCSS};
  ${withShadowCSS};

  flex-direction: column;

  ${({ flex }) => css`
    ${flex && `flex: ${flex};`}
  `}
`;

export const ActionButton: React.FC<
  React.PropsWithChildren<ActionButtonProps>
> = ({
  children,
  isLoading: isLoadingProp = false,
  disabled,
  trailingIcon,
  leadingIcon,
  onClick,
  variant = 'primary',
  cursor = 'pointer',
  ...props
}) => {
  const [isLoading, setIsLoading] = useState(isLoadingProp);
  const { buttonRef, handleClick } = useButtonClickBehavior({
    onClick,
    isLoading,
    setIsLoading,
  });
  const { colors } = useCurrentTheme();
  const loadingIconColor = colors.button[variant].text;

  useEffect(() => {
    setIsLoading(isLoadingProp);
  }, [isLoadingProp]);

  return (
    <StyledButton
      ref={buttonRef}
      onClick={handleClick}
      isLoading={isLoading}
      disabled={disabled || isLoading}
      aria-disabled={disabled || isLoading}
      variant={variant}
      cursor={cursor}
      {...props}
    >
      <Box
        as="span"
        visibility={isLoading ? 'hidden' : 'visible'}
        flexDirection="row"
        justifyContent="center"
        gap={2}
        alignItems="center"
      >
        {leadingIcon && <Icon size="sm" icon={leadingIcon} />}
        {children}
        {trailingIcon && <Icon size="md" icon={trailingIcon} />}
      </Box>
      {isLoading && (
        <Icon
          size="md"
          icon={Loading}
          position="absolute"
          transform="scaleX(-1)"
          spin={true}
          fillColor="none"
          strokeColor={loadingIconColor}
        />
      )}
    </StyledButton>
  );
};

export const ActionButtonWithCommonTheme = (props: ActionButtonProps) => (
  <ThemeWrapper themeName="common">
    <ActionButton {...props} />
  </ThemeWrapper>
);
